import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import {
  demands,
  pipes,
  type DemandPriority,
  type OperationDetail,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

const prioridades: { value: DemandPriority; label: string }[] = [
  { value: 'urgente', label: 'Urgente' },
  { value: 'alta', label: 'Alta' },
  { value: 'normal', label: 'Normal' },
  { value: 'baixa', label: 'Baixa' },
];

/**
 * Responsavel, prioridade e a esteira da operacao (P-EST-1).
 *
 * Antes o responsavel e a prioridade so mudavam por chamada direta a API (defeito 6
 * de "Operacao e esteira" do 47, anexo C). A troca grava pela atribuicao da fila, que
 * leva o mesmo responsavel e a mesma prioridade ao cartao corrente da esteira. O
 * cartao mostra em que esteira e coluna a operacao esta, com o caminho para o quadro.
 */
export function EsteiraDaOperacao({ operacao }: { operacao: OperationDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const podeVerEsteira = can('esteiras.esteira.view');
  const podeAtribuir = can('demandas.demanda.update');

  const cartoes = useQuery({
    queryKey: ['operations', operacao.id, 'card'],
    queryFn: ({ signal }) => pipes.operationCards(operacao.id, signal),
    enabled: podeVerEsteira,
  });
  const atual = cartoes.data?.current ?? null;

  const responsaveis = useQuery({
    queryKey: ['pipes', atual?.pipe.id, 'owners'],
    queryFn: ({ signal }) => pipes.owners(atual?.pipe.id ?? '', signal),
    enabled: podeVerEsteira && podeAtribuir && atual !== null,
    staleTime: 5 * 60_000,
  });

  const atribuir = useMutation({
    mutationFn: (input: { owner_id?: string | null; priority?: DemandPriority }) =>
      demands.assign(operacao.id, input),
    onSuccess: () => {
      toast.success('Atribuicao atualizada.');
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
      void queryClient.invalidateQueries({ queryKey: ['demands'] });
      void queryClient.invalidateQueries({ queryKey: ['pipes'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const opcoesDeResponsavel = (responsaveis.data ?? []).map((o) => ({
    value: o.member_id,
    label: o.name,
  }));
  if (
    operacao.owner_member_id &&
    !opcoesDeResponsavel.some((o) => o.value === operacao.owner_member_id)
  ) {
    opcoesDeResponsavel.unshift({
      value: operacao.owner_member_id,
      label: operacao.owner_label || 'Responsavel atual',
    });
  }

  return (
    <Card>
      <CardHeader
        title="Esteira e atribuicao"
        description="O responsavel e a prioridade valem para a operacao e para o cartao da esteira."
      />
      <CardBody className="flex flex-col gap-3">
        {podeVerEsteira && cartoes.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar a esteira">
            {cartoes.error.message}
          </Alert>
        ) : null}

        {podeVerEsteira && atual ? (
          <div className="flex flex-col gap-1">
            <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
              Esteira atual
            </p>
            <p className="flex flex-wrap items-center gap-1.5 text-sm text-ink-800">
              <span className="font-medium">{atual.pipe.name}</span>
              <span className="text-ink-400">&gt;</span>
              <span>{atual.step.name}</span>
              {atual.is_overdue ? <Badge tone="danger">Prazo vencido</Badge> : null}
            </p>
            {atual.due_at ? (
              <p className="text-2xs text-ink-500">
                Prazo: {formatDateTime(atual.due_at)}
              </p>
            ) : null}
            <Link
              to={`/esteiras/${atual.pipe.id}`}
              className="text-xs font-medium text-brand-700 hover:underline"
            >
              Ver no quadro da esteira
            </Link>
          </div>
        ) : podeVerEsteira && cartoes.isSuccess ? (
          <p className="text-sm text-ink-500">
            A operacao nao tem cartao aberto em esteira nesta etapa.
          </p>
        ) : null}

        {podeAtribuir && !operacao.is_terminal ? (
          <>
            <Select
              label="Responsavel"
              placeholder="Sem responsavel"
              disabled={atribuir.isPending || !podeVerEsteira}
              options={opcoesDeResponsavel}
              value={operacao.owner_member_id ?? ''}
              hint={
                podeVerEsteira
                  ? undefined
                  : 'A lista de responsaveis vem da esteira, que o seu perfil nao ve.'
              }
              onChange={(event) =>
                atribuir.mutate({ owner_id: event.target.value || '' })
              }
            />
            <Select
              label="Prioridade"
              disabled={atribuir.isPending}
              options={prioridades}
              value={operacao.priority ?? 'normal'}
              onChange={(event) =>
                atribuir.mutate({ priority: event.target.value as DemandPriority })
              }
            />
          </>
        ) : (
          <div className="flex flex-col gap-1 text-sm text-ink-700">
            <p>
              <span className="text-ink-500">Responsavel: </span>
              {operacao.owner_label || 'Sem responsavel'}
            </p>
            <p className="flex items-center gap-1.5">
              <span className="text-ink-500">Prioridade: </span>
              <StatusBadge kind="prioridade" value={operacao.priority ?? 'normal'} />
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
